import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Paperclip, Download, Play, FileText, Music, File, Loader2, ShieldCheck, Check, ScanText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useVault } from "@/context/VaultContext";
import { addAttachment, listFiles, fileDocName } from "@/lib/store";
import { downloadBlob, fmtBytes } from "@/lib/util";
import { VideoDialog } from "./VideoDialog";
import { AutoScanToggle, useAutoScan } from "./AutoScanToggle";
import { scannable } from "@/lib/scan";

export function useObjectUrl(blob) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!blob) return setUrl(null);
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);
  return url;
}

function Preview({ f, kids, onPlay }) {
  const visual = kids.find((k) => k.role === "stamped" || k.role === "thumb");
  const url = useObjectUrl(f.kind === "photo" || f.kind === "video" ? (visual || f).blob : f.kind === "audio" ? f.blob : null);
  if (f.kind === "photo") return <img src={url || ""} alt={f.name} className="w-full aspect-video object-contain bg-black" />;
  if (f.kind === "video") return (
    <button type="button" onClick={onPlay} className="relative w-full aspect-video bg-black" data-testid={`play-video-${f.id}`}>
      {visual && <img src={url || ""} alt={f.name} className="w-full h-full object-cover" />}
      <span className="absolute inset-0 flex items-center justify-center"><span className="h-12 w-12 rounded-full bg-black/60 flex items-center justify-center"><Play className="h-5 w-5 text-white fill-white" /></span></span>
    </button>
  );
  if (f.kind === "audio") return <div className="p-3 bg-muted aspect-video flex flex-col justify-center gap-2"><Music className="h-6 w-6 text-primary" /><audio controls src={url || undefined} className="w-full" /></div>;
  const Icon = f.kind === "pdf" || f.kind === "text" ? FileText : File;
  return <div className="aspect-video bg-muted flex items-center justify-center"><Icon className="h-10 w-10 text-primary" /></div>;
}

function AttachmentCard({ f, kids, note, onPlay, onScan }) {
  const [scanning, setScanning] = useState(false);
  const scan = async () => { setScanning(true); await onScan(f); setScanning(false); };
  const { profile, folderName } = useVault();
  const dl = (file) => downloadBlob(file.blob, fileDocName(file, note, folderName(note.folderId), profile));
  const stamped = kids.find((k) => k.role === "stamped");
  const thumb = kids.find((k) => k.role === "thumb");
  const frames = kids.filter((k) => k.role === "frame");
  return (
    <div className="rounded-lg border bg-card overflow-hidden" data-testid={`attachment-${f.id}`}>
      <Preview f={f} kids={kids} onPlay={onPlay} />
      <div className="p-3 space-y-2">
        <p className="text-sm font-medium truncate" title={f.name}>{f.name}</p>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="secondary" className="text-[10px] uppercase">{f.kind}</Badge>
          <span className="text-xs font-mono text-muted-foreground">{fmtBytes(f.size)}</span>
          {(stamped || thumb) && <Badge variant="outline" className="text-[10px] gap-1 text-verified border-verified/40"><ShieldCheck className="h-3 w-3" />watermarked</Badge>}
        </div>
        <Badge variant="outline" className="text-[10px] gap-1" data-testid={`attachment-verified-${f.id}`}><Check className="h-3 w-3" />Timestamped & Hash-Sealed</Badge>
        <div className="flex flex-wrap gap-1.5">
          {stamped && <Button size="sm" className="h-7 text-xs" onClick={() => dl(stamped)} data-testid={`download-stamped-${f.id}`}><Download className="h-3 w-3 mr-1" />Stamped</Button>}
          {thumb && <Button size="sm" variant="secondary" className="h-7 text-xs" onClick={() => dl(thumb)} data-testid={`download-thumb-${f.id}`}><Download className="h-3 w-3 mr-1" />Thumb</Button>}
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => dl(f)} data-testid={`download-original-${f.id}`}><Download className="h-3 w-3 mr-1" />Original</Button>
          {onScan && scannable(f) && (
            <Button size="sm" variant="secondary" className="h-7 text-xs" onClick={scan} disabled={scanning} data-testid={`scan-attachment-${f.id}`}>
              {scanning ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <ScanText className="h-3 w-3 mr-1" />}AI Scan / Extract Text
            </Button>
          )}
          {frames.map((fr, i) => <Button key={fr.id} size="sm" variant="ghost" className="h-7 text-xs font-mono" onClick={() => dl(fr)} data-testid={`download-frame-${fr.id}`}>Frame {i + 1}</Button>)}
        </div>
      </div>
    </div>
  );
}

export function Attachments({ note, onScan }) {
  const [autoScan, setAutoScan] = useAutoScan();
  const { profile, assets } = useVault();
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(0);
  const [video, setVideo] = useState(null);
  const input = useRef(null);
  const camera = useRef(null);
  const load = useCallback(() => listFiles(note.id).then(setFiles), [note.id]);
  useEffect(() => { load(); }, [load]);
  const pick = async (e) => {
    const list = [...e.target.files]; e.target.value = "";
    setBusy(list.length);
    const added = [];
    for (const f of list) { added.push(await addAttachment(note, f, { profile, assets })); setBusy((b) => b - 1); await load(); }
    toast.success(`${list.length} file${list.length === 1 ? "" : "s"} attached`, { description: "Timestamped & watermarked copies created" });
    if (autoScan && onScan) for (const f of added.filter(scannable)) await onScan(f);
  };
  const originals = files.filter((f) => f.role === "original");
  return (
    <div data-testid="attachments-section">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-xs font-semibold uppercase tracking-widest whitespace-nowrap">Attachments ({originals.length})</span>
        {onScan && <span className="ml-auto whitespace-nowrap"><AutoScanToggle on={autoScan} onChange={setAutoScan} testid="attachments-autoscan-switch" /></span>}
        <div className="flex gap-2 w-full sm:w-auto">
        <Button size="sm" variant="outline" className="flex-1 sm:flex-none" onClick={() => camera.current.click()} disabled={busy > 0} data-testid="take-photo-button">
          <Camera className="h-4 w-4 mr-1" />Take Photo
        </Button>
        <input ref={camera} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="take-photo-input" />
        <Button size="sm" variant="outline" className="flex-1 sm:flex-none" onClick={() => input.current.click()} disabled={busy > 0} data-testid="attach-file-button">
          {busy > 0 ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Paperclip className="h-4 w-4 mr-1" />}
          {busy > 0 ? `Processing ${busy}…` : "Attach files"}
        </Button>
        </div>
        <input ref={input} type="file" multiple className="hidden" accept=".pdf,.txt,.jpg,.jpeg,.png,application/pdf,text/plain,image/jpeg,image/png,image/*,video/*,audio/*" onChange={pick} data-testid="attach-file-input" />
      </div>
      <p className="text-xs text-muted-foreground -mt-1 mb-3" data-testid="attach-accepted-types">Accepted: PDF, JPG, PNG, TXT • Also voice memos, audio &amp; video</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {originals.map((f) => <AttachmentCard key={f.id} f={f} kids={files.filter((k) => k.parentId === f.id)} note={note} onPlay={() => setVideo(f)} onScan={onScan} />)}
      </div>
      {video && <VideoDialog file={video} note={note} onClose={() => setVideo(null)} onSaved={load} />}
    </div>
  );
}
